import { useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import {
  buildZaNavState,
  isWorkspaceCabinetPath,
  mergeLocationState,
  pickRoleForPath,
  rememberReturnTo,
  type ZaLocationState,
} from '@/lib/navigationState';

/**
 * Keeps activeRole + history.state.zaNav aligned with the URL so
 * Back / Forward / refresh restore the correct cabinet context
 * without remounting AppLayout or duplicating shell handlers.
 */
export const NavigationStateSync = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, switchRole } = useAuth();
  const patchedRef = useRef<string>('');
  const rolesKey = user?.roles?.join(',') ?? '';
  const activeRole = user?.activeRole;

  useLayoutEffect(() => {
    if (!user) return;

    const { pathname, search, hash, state } = location;
    const zaNav = (state as ZaLocationState | null)?.zaNav;
    const stateRole = zaNav?.role;
    const ownerPreview = Boolean(
      zaNav?.preview && (activeRole === 'SYSTEM_OWNER' || user.roles.includes('SYSTEM_OWNER'))
    );

    // Owner category preview must not switch activeRole into the previewed category.
    const pathRole = ownerPreview
      ? null
      : pickRoleForPath(pathname, user.roles, stateRole ?? activeRole);

    if (
      !ownerPreview &&
      pathRole &&
      pathRole !== activeRole &&
      user.roles.includes(pathRole)
    ) {
      switchRole(pathRole);
    }

    if (isWorkspaceCabinetPath(pathname)) {
      rememberReturnTo(pathname);
    }

    if (!isWorkspaceCabinetPath(pathname)) return;

    const desired = buildZaNavState({
      pathname,
      role: ownerPreview ? activeRole : pathRole ?? stateRole ?? activeRole,
      category: zaNav?.category,
      preview: ownerPreview ? zaNav?.preview : undefined,
      returnTo: pathname,
    });
    const current = (state as ZaLocationState | null)?.zaNav;
    const patchKey = `${pathname}|${desired.role ?? ''}|${desired.preview ?? ''}|${desired.returnTo ?? ''}`;
    if (patchedRef.current === patchKey) return;

    const needsState =
      !current ||
      current.role !== desired.role ||
      current.preview !== desired.preview ||
      current.returnTo !== desired.returnTo;

    if (!needsState) {
      patchedRef.current = patchKey;
      return;
    }

    patchedRef.current = patchKey;
    navigate(`${pathname}${search}${hash}`, {
      replace: true,
      state: mergeLocationState(state, desired),
    });
  }, [activeRole, location, navigate, rolesKey, switchRole, user]);

  return null;
};
